import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ShieldCheck, Building2, UserPlus } from "lucide-react";
import { toast } from "sonner";

export default function Admin() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "owner", property_ids: [] });
  const [selProps, setSelProps] = useState([]);
  const [role, setRole] = useState("owner");

  const load = () => {
    Promise.all([api.get("/users"), api.get("/properties")]).then(([u, p]) => {
      setUsers(u.data);
      setProperties(p.data);
    });
  };
  useEffect(load, []);

  const openEdit = (u) => {
    setEditing(u);
    setSelProps(u.property_ids || []);
    setRole(u.role);
  };

  const toggleProp = (id) =>
    setSelProps((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const toggleNewUserProp = (id) =>
    setNewUser((u) => ({ ...u, property_ids: u.property_ids.includes(id) ? u.property_ids.filter((x) => x !== id) : [...u.property_ids, id] }));

  const save = async () => {
    try {
      await api.put(`/users/${editing.user_id}`, { role, property_ids: selProps });
      toast.success("Usuario actualizado");
      setEditing(null);
      load();
    } catch {
      toast.error("Error al actualizar");
    }
  };

  const propName = (id) => properties.find((p) => p.id === id)?.nombre || id;

  const openCreate = () => {
    setNewUser({ name: "", email: "", password: "", role: "owner", property_ids: [] });
    setCreating(true);
  };

  const createUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) return toast.error("Nombre, email y contraseña son obligatorios");
    try {
      await api.post("/users", newUser);
      toast.success("Usuario creado");
      setCreating(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al crear usuario");
    }
  };

  return (
    <div data-testid="admin-page">
      <PageHeader title="Administración" subtitle="Usuarios y accesos">
        <Button onClick={openCreate} data-testid="create-user-button"><UserPlus className="h-4 w-4 mr-1.5" /> Nuevo usuario</Button>
      </PageHeader>

      <div className="data-card overflow-hidden">
        <Table data-testid="users-table">
          <TableHeader><TableRow>
            <TableHead>Usuario</TableHead><TableHead>Email</TableHead>
            <TableHead>Rol</TableHead><TableHead>Pisos asignados</TableHead><TableHead className="w-24"></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                <TableCell className="font-medium flex items-center gap-2">
                  {u.picture && <img src={u.picture} alt="" className="h-7 w-7 rounded-full" />}
                  {u.name}
                </TableCell>
                <TableCell className="text-muted-foreground">{u.email}</TableCell>
                <TableCell>
                  {u.role === "admin"
                    ? <Badge className="bg-primary/15 text-primary hover:bg-primary/15 border-0"><ShieldCheck className="h-3 w-3 mr-1" />Admin</Badge>
                    : <Badge variant="outline">Propietario</Badge>}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {u.role === "admin" ? "Todos" : (u.property_ids?.length ? u.property_ids.map(propName).join(", ") : "—")}
                </TableCell>
                <TableCell>
                  <Button variant="outline" size="sm" onClick={() => openEdit(u)} disabled={u.user_id === me.user_id} data-testid={`edit-user-${u.user_id}`}>
                    Editar
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Editar {editing?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="overline mb-1.5">Rol</p>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid="role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="owner">Propietario</SelectItem>
                  <SelectItem value="admin">Administrador (acceso total)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {role === "owner" && (
              <div>
                <p className="overline mb-2">Pisos con acceso</p>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {properties.map((p) => (
                    <label key={p.id} className="flex items-center gap-3 rounded-md border border-border p-2.5 cursor-pointer hover:border-primary transition-colors">
                      <Checkbox checked={selProps.includes(p.id)} onCheckedChange={() => toggleProp(p.id)} data-testid={`assign-prop-${p.id}`} />
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">{p.nombre}</span>
                    </label>
                  ))}
                  {properties.length === 0 && <p className="text-sm text-muted-foreground">No hay pisos creados.</p>}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button onClick={save} data-testid="save-user-button">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Nuevo usuario</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nombre *</Label><Input value={newUser.name} onChange={(e) => setNewUser((u) => ({ ...u, name: e.target.value }))} /></div>
            <div><Label>Email *</Label><Input type="email" value={newUser.email} onChange={(e) => setNewUser((u) => ({ ...u, email: e.target.value }))} /></div>
            <div><Label>Contraseña inicial *</Label><Input type="password" value={newUser.password} onChange={(e) => setNewUser((u) => ({ ...u, password: e.target.value }))} /></div>
            <div>
              <p className="overline mb-1.5">Rol</p>
              <Select value={newUser.role} onValueChange={(v) => setNewUser((u) => ({ ...u, role: v, property_ids: v === "admin" ? [] : u.property_ids }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="owner">Propietario</SelectItem>
                  <SelectItem value="admin">Administrador (acceso total)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {newUser.role === "owner" && (
              <div>
                <p className="overline mb-2">Pisos con acceso</p>
                <div className="space-y-2 max-h-56 overflow-y-auto">
                  {properties.map((p) => (
                    <label key={p.id} className="flex items-center gap-3 rounded-md border border-border p-2.5 cursor-pointer hover:border-primary transition-colors">
                      <Checkbox checked={newUser.property_ids.includes(p.id)} onCheckedChange={() => toggleNewUserProp(p.id)} />
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">{p.nombre}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)}>Cancelar</Button>
            <Button onClick={createUser}>Crear usuario</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
